import React, { useEffect, useState } from 'react';
import { BookOpen, CheckCircle2, Circle } from 'lucide-react';
import KnowledgeBaseManager from '../components/KnowledgeBaseManager';
import { supabase } from '../utils/supabase';

interface DoubleSummary {
  id: string;
  name: string;
  status: 'active' | 'training' | 'inactive';
}

const KnowledgeBase = () => {
  const [doubles, setDoubles] = useState<DoubleSummary[]>([]);
  const [selectedDoubleId, setSelectedDoubleId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDoubles = async () => {
      try {
        setIsLoading(true);
        const { data, error: fetchError } = await supabase
          .from('doubles')
          .select('id, name, status')
          .order('created_at', { ascending: false });

        if (fetchError) throw fetchError;

        const fetched = (data || []) as DoubleSummary[];
        setDoubles(fetched);

        if (!selectedDoubleId && fetched.length > 0) {
          const active = fetched.find((item) => item.status === 'active');
          setSelectedDoubleId(active?.id || fetched[0].id);
        }
      } catch (err) {
        console.error('Error loading doubles:', err);
        setError('Failed to load doubles');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDoubles();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Knowledge Base</h1>
          <p className="text-gray-600 mt-2">
            Add, remove, and manage documents for any double.
          </p>
        </div>
        <div className="flex items-center gap-2 text-primary">
          <BookOpen className="h-5 w-5" />
          <span className="text-sm font-medium">Docs &amp; sources</span>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      ) : doubles.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-gray-200">
          <p className="text-gray-600">Create a double to start adding knowledge.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
          <aside className="bg-white rounded-xl border border-gray-200 p-4 h-fit">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Select a Double</h2>
            <div className="space-y-2">
              {doubles.map((double) => {
                const isSelected = double.id === selectedDoubleId;
                return (
                  <button
                    key={double.id}
                    type="button"
                    onClick={() => setSelectedDoubleId(double.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${
                      isSelected
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-transparent hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{double.name}</span>
                      {double.status === 'active' ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      ) : (
                        <Circle className="h-4 w-4 text-gray-300" />
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-1 capitalize">
                      {double.status}
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>

          <section className="bg-white rounded-xl border border-gray-200 p-6">
            {selectedDoubleId ? (
              <KnowledgeBaseManager doubleId={selectedDoubleId} />
            ) : (
              <div className="text-center py-12 text-gray-600">
                Select a double to manage its knowledge base.
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default KnowledgeBase;
